import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import './Wallet.css';

// =============================================================================
// FORMATTERS
// =============================================================================
const fmtNGN = (n) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2
  }).format(parseFloat(n || 0));

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' });
};

const maskAccount = (n) => {
  if (!n) return '';
  const s = String(n);
  return `${s.slice(0, 2)}••${s.slice(-3)}`;
};

const CATEGORY_LABELS = {
  FUNDING: 'Wallet top-up',
  CHOWDECK_DELIVERY: 'Chowdeck delivery',
  REFUND: 'Refund',
  ADJUSTMENT: 'Adjustment',
  REVERSAL: 'Reversal',
  PROMOTIONAL_CREDIT: 'Bonus credit',
  SALE_HOLD: 'Customer payment',
  SERVICE_CHARGE: 'Transaction fee',
  WITHDRAWAL: 'Withdrawal',
  CHARGEBACK: 'Chargeback',
  SUBSCRIPTION_CHARGE: 'Subscription',
  POS_FEE_ACCRUAL: 'Direct sale transaction fee'
};

const SOURCE_LABELS = {
  VIRTUAL_TERMINAL: 'Paystack Terminal',
  DVA: 'Legacy bank transfer',
  PAYSTACK_CHECKOUT: 'Online checkout (Paystack)',
  FLUTTERWAVE_CHECKOUT: 'Online checkout (Flutterwave)',
  STOREFRONT: 'Storefront manual collection',
  WHATSAPP: 'WhatsApp manual collection',
  DIRECT_COLLECTION: 'Direct manual collection',
  POS: 'POS sale',
  SUBSCRIPTION: 'Subscription',
  WITHDRAWAL: 'Withdrawal',
  CHARGEBACK: 'Chargeback',
  ADMIN: 'Admin action',
  CHOWDECK: 'Chowdeck'
};

const STATUS_OPTIONS = [
  { value: '',                  label: 'All' },
  { value: 'PENDING,ON_HOLD',   label: 'Pending' },
  { value: 'COMPLETED',         label: 'Completed' },
  { value: 'FAILED',            label: 'Failed' },
  { value: 'REVERSED',          label: 'Reversed' }
];

const SOURCE_OPTIONS = [
  { value: '',                     label: 'All' },
  { value: 'VIRTUAL_TERMINAL',      label: 'Paystack Terminal' },
  { value: 'DVA',                  label: 'Legacy bank transfer' },
  { value: 'PAYSTACK_CHECKOUT',    label: 'Online checkout (Paystack)' },
  { value: 'FLUTTERWAVE_CHECKOUT', label: 'Online checkout (Flutterwave)' },
  { value: 'STOREFRONT',           label: 'Storefront manual collection' },
  { value: 'WHATSAPP',             label: 'WhatsApp manual collection' },
  { value: 'DIRECT_COLLECTION',    label: 'Direct manual collection' },
  { value: 'POS',                  label: 'POS sale' },
  { value: 'CHOWDECK',             label: 'Chowdeck' },
  { value: 'SUBSCRIPTION',         label: 'Subscription' },
  { value: 'WITHDRAWAL',           label: 'Withdrawal' },
  { value: 'CHARGEBACK',           label: 'Chargeback' },
  { value: 'ADMIN',                label: 'Admin action' }
];

const TYPE_OPTIONS = [
  { value: '',       label: 'All' },
  { value: 'CREDIT', label: 'Money in' },
  { value: 'DEBIT',  label: 'Money out' }
];

const DIRECT_COLLECTION_LABELS = {
  STOREFRONT: 'Storefront manual collection',
  WHATSAPP: 'WhatsApp manual collection',
  MANUAL: 'Manual sale collection',
  DIRECT_COLLECTION: 'Direct manual collection',
};

const getTransactionSourceKey = (tx = {}) => {
  if (tx.category === 'POS_FEE_ACCRUAL' && tx.source === 'POS') {
    const orderChannel = tx.metadata?.orderChannel || tx.metadata?.channel;
    if (orderChannel && orderChannel !== 'POS') return orderChannel;
  }
  return tx.source;
};

const getTransactionSourceLabel = (tx = {}) => {
  const sourceKey = getTransactionSourceKey(tx);
  if (!sourceKey) return '';
  if (tx.category === 'POS_FEE_ACCRUAL' && DIRECT_COLLECTION_LABELS[sourceKey]) {
    return DIRECT_COLLECTION_LABELS[sourceKey];
  }
  return SOURCE_LABELS[sourceKey] || sourceKey;
};

const DATE_PRESETS = [
  { value: 'all',       label: 'All' },
  { value: 'thisWeek',  label: 'This Week' },
  { value: 'lastWeek',  label: 'Last Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisYear',  label: 'This Year' },
  { value: 'lastYear',  label: 'Last Year' },
  { value: 'custom',    label: 'Custom Date' }
];

const EMPTY_FILTERS = {
  type: '',
  category: '',
  status: '',
  source: '',
  datePreset: 'all',
  startDate: '',
  endDate: ''
};

function getInitialFilters() {
  if (typeof window === 'undefined') return EMPTY_FILTERS;

  const params = new URLSearchParams(window.location.search);
  const source = params.get('source') || '';
  const validSource = SOURCE_OPTIONS.some(option => option.value === source) ? source : '';

  return {
    ...EMPTY_FILTERS,
    source: validSource
  };
}

// Monday-start week (en-NG convention).
function computeDateRange(preset) {
  const now = new Date();
  const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
  const startOfWeek = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay(); // 0=Sun, 1=Mon...
    const diff = day === 0 ? -6 : 1 - day;
    x.setDate(x.getDate() + diff);
    return x;
  };
  switch (preset) {
    case 'thisWeek':
      return { start: startOfWeek(now), end: now };
    case 'lastWeek': {
      const start = startOfWeek(now);
      start.setDate(start.getDate() - 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start, end: endOfDay(end) };
    }
    case 'thisMonth':
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    case 'lastMonth':
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end:   new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
      };
    case 'thisYear':
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    case 'lastYear':
      return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end:   new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999)
      };
    default:
      return { start: null, end: null };
  }
}

// =============================================================================
// WALLET PAGE
// =============================================================================
export default function Wallet() {
  const { getAxios } = useSecureAuth();
  const toast = useToast();

  const [wallet, setWallet] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityError, setEligibilityError] = useState(null);
  const [dva, setDva] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);

  // Modals
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showFund, setShowFund] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Click-to-expand on withdrawal + activity rows
  const [expandedWithdrawalId, setExpandedWithdrawalId] = useState(null);
  const [expandedActivityId, setExpandedActivityId] = useState(null);

  // Tracks which withdrawals were in-flight on the previous poll so we know
  // when one transitions to a terminal state and need to refetch the wallet
  // balance + activity log (the webhook updates the WalletTransaction debit).
  const prevInFlightRef = useRef(new Set());

  // Activity filters
  const [filters, setFilters] = useState(getInitialFilters);
  const [page, setPage] = useState(1);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.type) n++;
    if (filters.category) n++;
    if (filters.status) n++;
    if (filters.source) n++;
    if (filters.datePreset && filters.datePreset !== 'all') n++;
    return n;
  }, [filters]);

  // Fetch wallet summary + eligibility + legacy DVA + recent withdrawals in parallel.
  // Each call is allowed to fail independently — DVA might 404 on a brand-new
  // merchant, eligibility might 500 if any of its downstream services blow up,
  // but balance is load-bearing for the page to render at all.
  const fetchAll = useCallback(async () => {
    const axios = getAxios();
    try {
      const [balanceRes, eligRes, dvaRes, withdrawalsRes] = await Promise.all([
        axios.get('/wallet/balance'),
        axios.get('/wallet/withdrawal-eligibility').catch(err => {
          console.error('[Wallet] eligibility failed:', err.response?.status, err.response?.data || err.message);
          return { __error: err.response?.data?.error || err.message || 'Unknown error', __status: err.response?.status };
        }),
        axios.get('/dva/my-account').catch(() => ({ data: null })),
        axios.get('/wallet/withdrawals?limit=5').catch(() => ({ data: null }))
      ]);

      if (balanceRes.data.success) setWallet(balanceRes.data);

      if (eligRes.__error) {
        setEligibilityError(eligRes.__error);
        setEligibility(null);
      } else if (eligRes.data?.success === false) {
        setEligibilityError(eligRes.data.error || 'Unknown error');
        setEligibility(null);
      } else if (eligRes.data?.success) {
        setEligibilityError(null);
        setEligibility(eligRes.data);
      }

      if (dvaRes.data?.success && dvaRes.data.dva) setDva(dvaRes.data.dva);
      if (withdrawalsRes.data?.items) setWithdrawals(withdrawalsRes.data.items);
    } catch (err) {
      console.error('[Wallet] load error:', err);
      toast.error('Failed to load wallet');
    }
  }, [getAxios, toast]);

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const axios = getAxios();
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (filters.type) params.set('type', filters.type);
      if (filters.category) params.set('category', filters.category);
      if (filters.status) params.set('status', filters.status);
      if (filters.source) params.set('source', filters.source);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      const r = await axios.get(`/wallet/transactions?${params.toString()}`);
      if (r.data.success) {
        setTransactions(r.data.transactions || []);
        setPagination(r.data.pagination || null);
      }
    } catch (err) {
      console.error('[Wallet] transactions error:', err);
    } finally {
      setTxLoading(false);
    }
  }, [getAxios, page, filters]);

  // Lighter fetch used by the polling loop — only refreshes the withdrawals
  // list, not the entire page. If a previously in-flight withdrawal moves to
  // a terminal state, fires fetchAll + fetchTransactions once so the balance
  // and activity log catch up with the webhook commit.
  const fetchWithdrawals = useCallback(async () => {
    try {
      const r = await getAxios().get('/wallet/withdrawals?limit=5');
      const items = r.data?.items || [];
      const freshInFlight = new Set(
        items.filter(w => w.status === 'PENDING' || w.status === 'PROCESSING').map(w => w.id)
      );
      const transitioned = [...prevInFlightRef.current].some(id => !freshInFlight.has(id));
      setWithdrawals(items);
      prevInFlightRef.current = freshInFlight;
      if (transitioned) {
        fetchAll();
        fetchTransactions();
      }
    } catch (err) {
      console.error('[Wallet] poll withdrawals error:', err);
    }
  }, [getAxios, fetchAll, fetchTransactions]);

  const applyFilters = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    applyFilters(EMPTY_FILTERS);
  }, [applyFilters]);

  const downloadExport = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (filters.type) params.set('type', filters.type);
      if (filters.category) params.set('category', filters.category);
      if (filters.status) params.set('status', filters.status);
      if (filters.source) params.set('source', filters.source);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);

      const r = await getAxios().get(`/wallet/transactions/export?${params.toString()}`, { responseType: 'blob' });

      const blob = new Blob([r.data], { type: r.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wallet-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Wallet] export error:', err);
      toast.error('Export failed — try a narrower date range.');
    }
  };

  // Initial load
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await fetchAll();
      if (alive) await fetchTransactions();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [fetchAll, fetchTransactions]);

  // Reload tx on filter / page change after initial
  useEffect(() => {
    if (!loading) fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  // Poll while any withdrawal is in flight. Stops automatically once all
  // visible rows reach a terminal state (COMPLETED / FAILED / REVERSED).
  useEffect(() => {
    const inFlight = withdrawals.some(w => w.status === 'PENDING' || w.status === 'PROCESSING');
    if (!inFlight) {
      prevInFlightRef.current = new Set();
      return;
    }
    // Seed prevInFlightRef on first turn so transitions get detected.
    if (prevInFlightRef.current.size === 0) {
      prevInFlightRef.current = new Set(
        withdrawals.filter(w => w.status === 'PENDING' || w.status === 'PROCESSING').map(w => w.id)
      );
    }
    const id = setInterval(fetchWithdrawals, 20000);
    return () => clearInterval(id);
  }, [withdrawals, fetchWithdrawals]);

  // Paystack-redirect verification (Chowdeck top-up flow)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('funding') === 'complete') {
      const reference = params.get('reference');
      if (reference) {
        (async () => {
          try {
            const r = await getAxios().post('/wallet/fund/verify', { reference });
            if (r.data.success) {
              toast.success(`Wallet funded! New balance: ${fmtNGN(r.data.balanceAfter)}`);
              fetchAll();
              fetchTransactions();
            } else {
              toast.error(r.data.error || 'Failed to verify payment');
            }
          } catch (e) {
            toast.error(e.response?.data?.error || 'Failed to verify payment');
          }
        })();
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyDva = () => {
    if (!dva?.accountNumber) return;
    navigator.clipboard.writeText(dva.accountNumber);
    toast.success('Account number copied');
  };

  const isFrozen = wallet?.status && wallet.status !== 'ACTIVE';

  if (loading) {
    return (
      <div className="mw-page">
        <div className="mw-skeleton">Loading your wallet…</div>
      </div>
    );
  }

  return (
    <div className="mw-page">
      <div className="mw-header">
        <div>
          <h1>Wallet</h1>
          <p>Your platform balance, payouts, and activity.</p>
        </div>
      </div>

      {isFrozen && (
        <div className="mw-banner mw-banner-danger">
          <strong>Wallet paused.</strong> Withdrawals are disabled while we review. We'll email you when it's active again.
        </div>
      )}

      {/* BALANCE CARDS — Available (primary), On Hold, Pending Withdrawal */}
      <div className="mw-balance-cards">
        <div className="mw-bal-card mw-bal-card-primary">
          <button
            className="mw-refresh-btn"
            onClick={() => { fetchAll(); fetchTransactions(); }}
            aria-label="Refresh balance"
            title="Refresh"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
          </button>
          <div className="mw-bal-label">Available Balance</div>
          <div className="mw-bal-amount">{fmtNGN(eligibility?.wallet?.availableBalance ?? 0)}</div>

          <button
            className="mw-btn mw-btn-primary mw-bal-cta"
            disabled={isFrozen || !(eligibility?.canWithdraw)}
            onClick={() => setShowWithdraw(true)}
            title={!eligibility?.canWithdraw && eligibility?.blockers?.[0] ? eligibility.blockers[0] : ''}
          >
            Withdraw funds
          </button>

          <button className="mw-bal-link" onClick={() => setShowFund(true)} disabled={isFrozen}>
            + Top up wallet
          </button>

          {!eligibility?.canWithdraw && eligibility?.blockers?.length > 0 && !isFrozen && (
            <div className="mw-bal-hint">{eligibility.blockers[0]}</div>
          )}
        </div>

        <div className="mw-bal-card mw-bal-card-light">
          <div className="mw-bal-label">Pending Withdrawal</div>
          <div className="mw-bal-amount mw-bal-amount-light">{fmtNGN(eligibility?.wallet?.pendingWithdrawalBalance ?? 0)}</div>
          <div className="mw-bal-note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>In transit to your bank. Updates once Paystack confirms the transfer.</span>
          </div>
        </div>

        <div className="mw-bal-card mw-bal-card-light">
          <div className="mw-bal-label">On Hold</div>
          <div className="mw-bal-amount mw-bal-amount-light">{fmtNGN(eligibility?.wallet?.onHoldBalance ?? 0)}</div>
          <div className="mw-bal-note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span>Releases to Available when you mark the order shipped.</span>
          </div>
        </div>
      </div>

      {/* INFO CARDS GRID */}
      <div className="mw-info-grid">
        {/* Overdraft */}
        {(() => {
          const cap = eligibility?.wallet?.overdraftLimit ?? 0;
          const used = eligibility?.wallet?.overdraftUsed ?? 0;
          const remaining = Math.max(0, cap - used);
          return (
            <InfoCard title="Overdraft" tone={used > 0 ? 'warning' : 'neutral'}>
              <div className="mw-info-row">
                <span>Cap</span>
                <strong>{fmtNGN(cap)}</strong>
              </div>
              <div className="mw-info-row">
                <span>Used</span>
                <strong className={used > 0 ? 'mw-text-warning' : ''}>{fmtNGN(used)}</strong>
              </div>
              <div className="mw-info-row">
                <span>Remaining</span>
                <strong>{fmtNGN(remaining)}</strong>
              </div>
              <p className="mw-info-note">
                Transaction fees on POS / direct bank transfers may use your overdraft. Top up to restore your buffer.
              </p>
            </InfoCard>
          );
        })()}

        {/* KYC limits — always render real data when eligibility loaded.
            Only show "Complete KYC" when the backend says NOT_STARTED.
            When eligibility failed to load entirely, surface that — don't
            blame it on KYC. */}
        <InfoCard title="Withdrawal limits">
          {!eligibility ? (
            <p className="mw-info-note">
              Couldn't load withdrawal info{eligibilityError ? `: ${eligibilityError}` : '.'}{' '}
              <button className="mw-info-link mw-info-link-btn" onClick={fetchAll}>Retry</button>
            </p>
          ) : eligibility.kyc.status === 'NOT_STARTED' ? (
            <p className="mw-info-note">
              Complete KYC to enable withdrawals.{' '}
              <a href="/settings/kyc" className="mw-info-link">Start KYC →</a>
            </p>
          ) : (
            <>
              <div className="mw-info-row">
                <span>KYC level</span>
                <strong>{eligibility.kyc.level.replace('LEVEL_', 'Level ')} · {eligibility.kyc.status}</strong>
              </div>
              <div className="mw-info-row">
                <span>Daily</span>
                <strong>
                  {eligibility.kyc.dailyLimit === null
                    ? <span className="mw-info-faded">No cap</span>
                    : <>{fmtNGN(eligibility.kyc.dailyRemaining)} <span className="mw-info-faded">of {fmtNGN(eligibility.kyc.dailyLimit)}</span></>}
                </strong>
              </div>
              <div className="mw-info-row">
                <span>Monthly</span>
                <strong>
                  {eligibility.kyc.monthlyLimit === null
                    ? <span className="mw-info-faded">No cap</span>
                    : <>{fmtNGN(eligibility.kyc.monthlyRemaining)} <span className="mw-info-faded">of {fmtNGN(eligibility.kyc.monthlyLimit)}</span></>}
                </strong>
              </div>
              {eligibility.bank ? (
                <div className="mw-info-row">
                  <span>Bank</span>
                  <strong>{eligibility.bank.bankName} {maskAccount(eligibility.bank.accountNumber)}</strong>
                </div>
              ) : (
                <p className="mw-info-note">
                  Add a verified bank account in KYC settings to withdraw.{' '}
                  <a href="/settings/kyc" className="mw-info-link">Open KYC →</a>
                </p>
              )}
            </>
          )}
        </InfoCard>

        {/* Legacy DVA */}
        {dva && (
          <InfoCard title="Legacy bank-transfer account">
            <div className="mw-dva-bank">{dva.bankName || 'Wema Bank'}</div>
            <div className="mw-dva-number" onClick={copyDva} title="Tap to copy">
              {dva.accountNumber}
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginLeft: 6 }}>
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
            </div>
            <div className="mw-dva-name">{dva.accountName}</div>
            <p className="mw-info-note">
              Kept for older DVA transfers and reconciliation. New order bank transfers should use Paystack Terminal.
            </p>
          </InfoCard>
        )}
      </div>

      {/* RECENT WITHDRAWALS */}
      {withdrawals.length > 0 && (
        <div className="mw-section">
          <div className="mw-section-header">
            <h2>Recent withdrawals</h2>
            {withdrawals.some(w => w.status === 'PENDING' || w.status === 'PROCESSING') && (
              <span className="mw-live-dot" title="Auto-refreshing while in flight">
                <span className="mw-live-dot-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="mw-list">
            {withdrawals.map(w => {
              const expanded = expandedWithdrawalId === w.id;
              const toggle = () => setExpandedWithdrawalId(expanded ? null : w.id);
              return (
                <div key={w.id} className={`mw-wd-row ${expanded ? 'mw-wd-row-expanded' : ''}`}>
                  <div
                    className="mw-wd-row-head"
                    onClick={toggle}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                    aria-expanded={expanded}
                  >
                    <div className="mw-wd-row-info">
                      <div className="mw-list-title">{fmtNGN(w.amount)} → {w.bankName}</div>
                      <div className="mw-list-sub">
                        {maskAccount(w.accountNumber)} · {fmtDateTime(w.initiatedAt)} · <StatusBadge status={w.status} />
                      </div>
                    </div>
                    <svg
                      className={`mw-wd-chevron ${expanded ? 'mw-wd-chevron-open' : ''}`}
                      width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {expanded && (
                    <div className="mw-wd-details">
                      <div className="mw-info-row">
                        <span>Reference</span>
                        <strong className="mw-mono">{w.withdrawalRef}</strong>
                      </div>
                      <div className="mw-info-row">
                        <span>Initiated</span>
                        <strong>{fmtDateTime(w.initiatedAt)}</strong>
                      </div>
                      {w.completedAt && (
                        <div className="mw-info-row">
                          <span>{w.status === 'FAILED' || w.status === 'REVERSED' ? 'Resolved' : 'Completed'}</span>
                          <strong>{fmtDateTime(w.completedAt)}</strong>
                        </div>
                      )}
                      <div className="mw-info-row">
                        <span>Account</span>
                        <strong>{w.accountName} · {maskAccount(w.accountNumber)}</strong>
                      </div>
                      {w.providerRef && (
                        <div className="mw-info-row">
                          <span>Reference</span>
                          <strong className="mw-mono">{w.providerRef}</strong>
                        </div>
                      )}
                      {w.failureReason && (
                        <div className="mw-wd-error">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                          </svg>
                          <span>{w.failureReason}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ACTIVITY LOG */}
      <div className="mw-section">
        <div className="mw-section-header">
          <h2>Activity</h2>
          <div className="mw-section-actions">
            <button className="mw-icon-btn" onClick={() => setShowFilters(true)} aria-label="Filter transactions">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              Filter
              {activeFilterCount > 0 && <span className="mw-filter-badge">{activeFilterCount}</span>}
            </button>
            <button className="mw-icon-btn" onClick={() => downloadExport('csv')}>CSV</button>
            <button className="mw-icon-btn" onClick={() => downloadExport('xlsx')}>Excel</button>
          </div>
        </div>

        <div className="mw-list">
          {txLoading && <div className="mw-empty">Loading…</div>}
          {!txLoading && transactions.length === 0 && (
            <div className="mw-empty">No transactions yet.</div>
          )}
          {!txLoading && transactions.map(tx => {
            const expanded = expandedActivityId === tx.id;
            const toggle = () => setExpandedActivityId(expanded ? null : tx.id);
            const sourceLabel = getTransactionSourceLabel(tx);
            // Audit-only rows (e.g. PAYSTACK_CHECKOUT / DVA service-charge log)
            // leave the balance untouched. POS_FEE_ACCRUAL and real debits move
            // the balance, so they still render in red below. The ledger is the
            // single source of truth — no channel / category list to maintain.
            const noBalanceImpact =
              parseFloat(tx.balanceBefore) === parseFloat(tx.balanceAfter);
            return (
              <div key={tx.id} className={`mw-tx-row ${expanded ? 'mw-tx-row-expanded' : ''}`}>
                <div
                  className="mw-list-row mw-tx-row-head"
                  onClick={toggle}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                  aria-expanded={expanded}
                >
                  <div>
                    <div className="mw-list-title">
                      {CATEGORY_LABELS[tx.category] || tx.category}
                      {noBalanceImpact && <span className="mw-record-badge">Record</span>}
                    </div>
                    <div className="mw-list-sub">
                      {sourceLabel ? `${sourceLabel} · ` : ''}
                      {fmtDateTime(tx.createdAt)}
                      {tx.orderNumber ? ` · Order ${tx.orderNumber}` : ''}
                      {tx.status !== 'COMPLETED' && (<> · <StatusBadge status={tx.status} /></>)}
                    </div>
                  </div>
                  <div className="mw-tx-row-right">
                    <div className={`mw-list-amt ${
                      noBalanceImpact ? 'mw-text-record'
                      : tx.type === 'DEBIT' ? 'mw-text-debit'
                      : 'mw-text-credit'
                    }`}>
                      {noBalanceImpact ? '' : tx.type === 'DEBIT' ? '−' : '+'}{fmtNGN(tx.amount)}
                    </div>
                    <svg
                      className={`mw-tx-chevron ${expanded ? 'mw-tx-chevron-open' : ''}`}
                      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {expanded && <ActivityDetails tx={tx} />}
              </div>
            );
          })}
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div className="mw-pager">
            <button disabled={pagination.page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span>Page {pagination.page} of {pagination.totalPages}</span>
            <button disabled={pagination.page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {showWithdraw && eligibility && (
        <WithdrawModal
          eligibility={eligibility}
          onClose={() => setShowWithdraw(false)}
          onSuccess={() => { setShowWithdraw(false); fetchAll(); fetchTransactions(); }}
          getAxios={getAxios}
          toast={toast}
        />
      )}

      {showFund && (
        <FundModal
          onClose={() => setShowFund(false)}
          getAxios={getAxios}
          toast={toast}
        />
      )}

      <FilterDrawer
        open={showFilters}
        current={filters}
        onClose={() => setShowFilters(false)}
        onApply={(next) => { applyFilters(next); setShowFilters(false); }}
        onClear={() => { clearFilters(); setShowFilters(false); }}
      />
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================
function InfoCard({ title, tone = 'neutral', children }) {
  return (
    <div className={`mw-info-card mw-info-card-${tone}`}>
      <h3>{title}</h3>
      <div className="mw-info-body">{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tone = ({
    PENDING: 'pending', ON_HOLD: 'pending', PROCESSING: 'pending',
    COMPLETED: 'success', SUCCESS: 'success',
    FAILED: 'danger', REVERSED: 'neutral'
  })[status] || 'neutral';
  return <span className={`mw-status mw-status-${tone}`}>{status}</span>;
}

function WithdrawModal({ eligibility, onClose, onSuccess, getAxios, toast }) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const max = eligibility.maxSingleWithdrawal;
  const min = eligibility.minimumWithdrawal;
  const presets = useMemo(() => {
    const vals = [min, 5000, 10000, 25000, 50000].filter(v => v >= min && v <= max);
    if (max >= min && !vals.includes(max)) vals.push(max);
    return [...new Set(vals)].sort((a, b) => a - b);
  }, [min, max]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (amt < min) { toast.error(`Minimum withdrawal is ${fmtNGN(min)}`); return; }
    if (amt > max) { toast.error(`You can withdraw up to ${fmtNGN(max)} right now`); return; }
    setSubmitting(true);
    try {
      const r = await getAxios().post('/wallet/withdraw', { amount: amt });
      if (r.data.success) {
        toast.success(`Withdrawal of ${fmtNGN(amt)} initiated. We'll email you when it lands.`);
        onSuccess();
      } else {
        toast.error(r.data.error || 'Withdrawal failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Withdraw to your bank" onClose={onClose}>
      <div className="mw-modal-summary">
        <div className="mw-info-row">
          <span>Available</span>
          <strong>{fmtNGN(eligibility.wallet.availableBalance)}</strong>
        </div>
        {eligibility.bank && (
          <div className="mw-info-row">
            <span>To</span>
            <strong>{eligibility.bank.bankName} {maskAccount(eligibility.bank.accountNumber)}</strong>
          </div>
        )}
        <div className="mw-info-row">
          <span>Daily remaining</span>
          <strong>
            {eligibility.kyc.dailyRemaining === null
              ? <span className="mw-info-faded">No cap</span>
              : fmtNGN(eligibility.kyc.dailyRemaining)}
          </strong>
        </div>
      </div>

      <label className="mw-field">
        <span>Amount (₦)</span>
        <input
          type="number"
          min={min}
          max={max}
          placeholder={`Min ${fmtNGN(min)}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>

      {presets.length > 0 && (
        <div className="mw-presets">
          {presets.map(v => (
            <button key={v} className="mw-preset" onClick={() => setAmount(String(v))}>
              {fmtNGN(v)}
            </button>
          ))}
        </div>
      )}

      <p className="mw-info-note">
        Transfers usually arrive within 30 minutes. We'll email you the moment it lands.
      </p>

      <div className="mw-modal-actions">
        <button className="mw-btn mw-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
        <button className="mw-btn mw-btn-primary" onClick={submit} disabled={submitting}>
          {submitting ? 'Sending…' : `Withdraw ${amount ? fmtNGN(amount) : ''}`}
        </button>
      </div>
    </Modal>
  );
}

function FundModal({ onClose, getAxios, toast }) {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const presets = [5000, 10000, 20000, 50000];

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!isFinite(amt) || amt < 1000) { toast.error('Minimum top-up is ₦1,000'); return; }
    if (amt > 5000000) { toast.error('Maximum single top-up is ₦5,000,000'); return; }
    setSubmitting(true);
    try {
      const r = await getAxios().post('/wallet/fund/initialize', { amount: amt });
      if (r.data.success) {
        window.location.href = r.data.authorizationUrl;
      } else {
        toast.error(r.data.error || 'Failed to start top-up');
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to start top-up');
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Top up your wallet" onClose={onClose}>
      <p className="mw-info-note">
        Pay securely via Paystack. Funds are available immediately. You can use them for Chowdeck dispatch, subscriptions, or any future platform service.
      </p>

      <label className="mw-field">
        <span>Amount (₦)</span>
        <input
          type="number"
          min={1000}
          placeholder="Min ₦1,000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>

      <div className="mw-presets">
        {presets.map(v => (
          <button key={v} className="mw-preset" onClick={() => setAmount(String(v))}>
            {fmtNGN(v)}
          </button>
        ))}
      </div>

      <div className="mw-modal-actions">
        <button className="mw-btn mw-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
        <button className="mw-btn mw-btn-primary" onClick={submit} disabled={submitting}>
          {submitting ? 'Loading…' : 'Continue to Paystack'}
        </button>
      </div>
    </Modal>
  );
}

// =============================================================================
// ACTIVITY DETAILS — drill-down for each WalletTransaction. Renders the
// category-specific fields the merchant cares about most. Pulls heavily from
// tx.metadata which the backend populates (settlementReference, totalAmount,
// serviceCharge, serviceChargeRate for SALE_HOLD; orderTotal + feeRate for
// POS_FEE_ACCRUAL; etc.). Falls back to a generic detail set if the category
// isn't recognised.
// =============================================================================
function ActivityDetails({ tx }) {
  const meta = tx.metadata || {};
  const profitability = meta.profitability || meta.feeBreakdown?.profitability || null;

  const rows = [];

  // Category-specific top rows (the "important" stuff)
  switch (tx.category) {
    case 'SALE_HOLD': {
      const gross = parseFloat(meta.totalAmount || 0);
      const fee = parseFloat(meta.serviceCharge || 0);
      if (gross > 0) rows.push(['Gross', fmtNGN(gross)]);
      if (fee > 0) rows.push(['Transaction fee', fmtNGN(fee)]);
      rows.push(['Net credited', <span className="mw-text-credit">{fmtNGN(tx.amount)}</span>]);
      if (tx.holdReleaseAt) rows.push(['Releases on', fmtDateTime(tx.holdReleaseAt)]);
      if (tx.releaseCondition) rows.push(['Release rule', tx.releaseCondition.replace(/_/g, ' ').toLowerCase()]);
      break;
    }
    case 'POS_FEE_ACCRUAL': {
      const orderTotal = parseFloat(meta.orderTotal || 0);
      if (orderTotal > 0) rows.push(['Order total', fmtNGN(orderTotal)]);
      rows.push(['Fee charged', <span className="mw-text-debit">{fmtNGN(tx.amount)}</span>]);
      break;
    }
    case 'SERVICE_CHARGE': {
      // Audit row — fee was deducted before money reached the wallet.
      // No balance impact, but we show the math so the merchant understands.
      const gross = parseFloat(meta.gross || 0);
      const net = parseFloat(meta.net || 0);
      if (gross > 0) rows.push(['Gross sale', fmtNGN(gross)]);
      rows.push(['Transaction fee', <span className="mw-text-record">{fmtNGN(tx.amount)}</span>]);
      if (profitability?.estimatedGatewayFeeAmount > 0) {
        rows.push(['Estimated payment provider fee', fmtNGN(profitability.estimatedGatewayFeeAmount)]);
      }
      if (profitability?.processorRecoveryShortfall > 0) {
        rows.push(['Provider fee gap', <span className="mw-text-debit">{fmtNGN(profitability.processorRecoveryShortfall)}</span>]);
      }
      if (net > 0) rows.push(['Credited to wallet', <span className="mw-text-credit">{fmtNGN(net)}</span>]);
      rows.push(['Wallet balance impact', <span className="mw-info-faded">None — fee taken at settlement</span>]);
      break;
    }
    case 'WITHDRAWAL': {
      rows.push(['Withdrawal amount', fmtNGN(tx.amount)]);
      break;
    }
    case 'CHARGEBACK': {
      rows.push(['Clawback', <span className="mw-text-debit">{fmtNGN(tx.amount)}</span>]);
      if (meta.resolution) rows.push(['Resolution', meta.resolution]);
      if (meta.providerDisputeId) rows.push(['Provider dispute', <span className="mw-mono">{meta.providerDisputeId}</span>]);
      break;
    }
    case 'FUNDING':
    case 'CHOWDECK_DELIVERY':
    case 'REFUND':
    case 'ADJUSTMENT':
    case 'REVERSAL':
    case 'PROMOTIONAL_CREDIT':
    case 'SUBSCRIPTION_CHARGE':
    default: {
      rows.push([tx.type === 'CREDIT' ? 'Credited' : 'Debited',
        <span className={tx.type === 'CREDIT' ? 'mw-text-credit' : 'mw-text-debit'}>
          {tx.type === 'DEBIT' ? '−' : '+'}{fmtNGN(tx.amount)}
        </span>]);
    }
  }

  // Common rows for every transaction
  rows.push(['Balance after', fmtNGN(tx.balanceAfter)]);
  if (tx.orderNumber) rows.push(['Order', tx.orderNumber]);
  if (tx.reference) rows.push(['Reference', <span className="mw-mono">{tx.reference}</span>]);
  rows.push(['Transaction ID', <span className="mw-mono">{tx.transactionRef}</span>]);
  if (tx.description) rows.push(['Note', tx.description]);

  const noBalanceImpact =
    parseFloat(tx.balanceBefore) === parseFloat(tx.balanceAfter);

  return (
    <div className="mw-tx-details">
      {noBalanceImpact && (
        <div className="mw-record-banner">
          For your records — no funds were taken from your wallet. The fee was deducted at settlement before the sale was credited.
        </div>
      )}
      {rows.map(([label, value], i) => (
        <div key={i} className="mw-info-row">
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function FilterDrawer({ open, current, onClose, onApply, onClear }) {
  const [draft, setDraft] = useState(current);

  // Re-sync local draft whenever the drawer opens.
  useEffect(() => { if (open) setDraft(current); }, [open, current]);

  const pickPreset = (preset) => {
    if (preset === 'custom' || preset === 'all') {
      setDraft(d => ({
        ...d,
        datePreset: preset,
        startDate: preset === 'all' ? '' : d.startDate,
        endDate:   preset === 'all' ? '' : d.endDate
      }));
      return;
    }
    const { start, end } = computeDateRange(preset);
    setDraft(d => ({
      ...d,
      datePreset: preset,
      startDate: start ? start.toISOString() : '',
      endDate:   end ? end.toISOString() : ''
    }));
  };

  const isoDay = (iso) => (iso ? iso.slice(0, 10) : '');

  return (
    <>
      <div
        className={`mw-drawer-backdrop ${open ? 'mw-drawer-open' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={`mw-drawer ${open ? 'mw-drawer-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Filter transactions"
      >
        <div className="mw-drawer-header">
          <h3>Filter Transactions</h3>
          <button className="mw-modal-close" onClick={onClose} aria-label="Close filters">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="mw-drawer-body">
          {/* Status */}
          <section className="mw-drawer-section">
            <h4>By Status</h4>
            <select
              className="mw-drawer-select"
              value={draft.status}
              onChange={(e) => setDraft(d => ({ ...d, status: e.target.value }))}
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </section>

          {/* Direction */}
          <section className="mw-drawer-section">
            <h4>Direction</h4>
            <select
              className="mw-drawer-select"
              value={draft.type}
              onChange={(e) => setDraft(d => ({ ...d, type: e.target.value }))}
            >
              {TYPE_OPTIONS.map(o => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </section>

          {/* Source */}
          <section className="mw-drawer-section">
            <h4>Source</h4>
            <select
              className="mw-drawer-select"
              value={draft.source}
              onChange={(e) => setDraft(d => ({ ...d, source: e.target.value }))}
            >
              {SOURCE_OPTIONS.map(o => (
                <option key={o.value || 'all'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </section>

          {/* Category */}
          <section className="mw-drawer-section">
            <h4>Category</h4>
            <select
              className="mw-drawer-select"
              value={draft.category}
              onChange={(e) => setDraft(d => ({ ...d, category: e.target.value }))}
            >
              <option value="">All</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </section>

          {/* Date */}
          <section className="mw-drawer-section">
            <h4>By Date</h4>
            <select
              className="mw-drawer-select"
              value={draft.datePreset}
              onChange={(e) => pickPreset(e.target.value)}
            >
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>

            {draft.datePreset === 'custom' && (
              <div className="mw-drawer-dates">
                <label>
                  <span>From</span>
                  <input
                    type="date"
                    value={isoDay(draft.startDate)}
                    onChange={(e) => setDraft(d => ({
                      ...d,
                      startDate: e.target.value ? new Date(e.target.value).toISOString() : ''
                    }))}
                  />
                </label>
                <label>
                  <span>To</span>
                  <input
                    type="date"
                    value={isoDay(draft.endDate)}
                    onChange={(e) => setDraft(d => ({
                      ...d,
                      endDate: e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : ''
                    }))}
                  />
                </label>
              </div>
            )}
          </section>
        </div>

        <div className="mw-drawer-footer">
          <button
            className="mw-btn mw-btn-secondary"
            onClick={onClear}
          >
            Clear all
          </button>
          <button
            className="mw-btn mw-btn-primary"
            onClick={() => onApply(draft)}
          >
            Apply
          </button>
        </div>
      </aside>
    </>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="mw-modal-overlay" onClick={onClose}>
      <div className="mw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mw-modal-header">
          <h3>{title}</h3>
          <button className="mw-modal-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div className="mw-modal-body">{children}</div>
      </div>
    </div>
  );
}
